/**
 * Bryte Mattress Dashboard — Hotel-facing view of installed smart mattresses,
 * live surge rate, rolling occupancy, and 30-day revenue.
 * Route: /bryte/:hotelId
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BedDouble, TrendingUp, DollarSign, Activity,
  Zap, Flame, Snowflake, Heart, Loader2, RefreshCw,
} from 'lucide-react';
import { BACKEND_URL } from '@/lib/backendUrl';

const TIER_META = {
  essential:   { label: 'Essential',   bg: 'bg-slate-500/10',  text: 'text-slate-300' },
  balance:     { label: 'Balance',     bg: 'bg-cyan-500/10',   text: 'text-cyan-400' },
  restorative: { label: 'Restorative', bg: 'bg-purple-500/10', text: 'text-purple-400' },
};

function StatCard({ icon: Icon, label, value, sub, accent = 'text-cyan-400' }) {
  return (
    <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accent}`} />
        <p className="text-[10px] text-[#64748b] uppercase tracking-wider">{label}</p>
      </div>
      <p className={`text-2xl font-bold font-mono ${accent}`}>{value}</p>
      {sub && <p className="text-[10px] text-[#64748b] mt-1">{sub}</p>}
    </div>
  );
}

function TierRateCard({ tier, data, occupancy }) {
  const meta = TIER_META[tier] || TIER_META.balance;
  const pct = ((data.current_rate_usd - data.floor) / (data.ceiling - data.floor)) * 100;
  return (
    <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] text-[#64748b] uppercase">{data.label}</p>
          <p className={`text-xl font-bold font-mono ${meta.text}`} data-testid={`rate-${tier}`}>
            ${data.current_rate_usd.toFixed(2)}<span className="text-xs text-[#64748b] font-normal"> /night</span>
          </p>
        </div>
        <Badge className={`${meta.bg} ${meta.text} text-[9px] border-0`}>{meta.label}</Badge>
      </div>
      <div className="h-1.5 bg-[#080c16] rounded-full overflow-hidden">
        <div
          className={`h-full ${meta.bg.replace('/10', '/60')}`}
          style={{ width: `${Math.max(2, Math.min(100, pct))}%` }}
        />
      </div>
      <div className="flex justify-between mt-1 text-[9px] text-[#64748b] font-mono">
        <span>${data.floor.toFixed(2)}</span>
        <span>Occ {occupancy.toFixed(1)}%</span>
        <span>${data.ceiling.toFixed(2)}</span>
      </div>
    </div>
  );
}

export default function BryteDashboardPage() {
  const { hotelId } = useParams();
  const hid = hotelId || 'hotel_grand_marina';
  const [mattresses, setMattresses] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [m, p, r] = await Promise.all([
        fetch(`${BACKEND_URL}/api/bryte/${hid}/mattresses`).then((x) => x.json()),
        fetch(`${BACKEND_URL}/api/bryte/pricing/${hid}`).then((x) => x.json()),
        fetch(`${BACKEND_URL}/api/bryte/revenue/${hid}`).then((x) => x.json()),
      ]);
      setMattresses(m.mattresses || []);
      setPricing(p);
      setRevenue(r);
    } finally {
      setLoading(false);
    }
  }, [hid]);

  useEffect(() => { refresh(); }, [refresh]);

  const settleNow = async () => {
    setSettling(true);
    try {
      await fetch(`${BACKEND_URL}/api/bryte/nightly-settle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hotel_id: hid }),
      });
      await refresh();
    } finally {
      setSettling(false);
    }
  };

  if (loading && !pricing) {
    return (
      <div className="min-h-screen bg-[#080c16] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080c16] text-[#f1f5f9]" data-testid="bryte-dashboard">
      {/* Header */}
      <div className="border-b border-[#1a2235] bg-[#0d1220]">
        <div className="max-w-6xl mx-auto px-4 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
              <BedDouble className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight" data-testid="bryte-title">Bryte Micro-Lease</h1>
              <p className="text-[11px] text-[#64748b] font-mono">{hid} · Dynamic occupancy-surge pricing</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-[#1a2235] bg-[#080c16] text-xs h-9"
              onClick={refresh}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
            <Button
              size="sm"
              disabled={settling}
              onClick={settleNow}
              className="bg-cyan-500 hover:bg-cyan-400 text-[#080c16] text-xs h-9 font-semibold"
              data-testid="settle-btn"
            >
              {settling ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Settle last night</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={BedDouble}
            label="Installed"
            value={pricing?.installed_units || 0}
            sub="Bryte smart beds"
            accent="text-cyan-400"
          />
          <StatCard
            icon={Activity}
            label="30d Occupancy"
            value={`${(pricing?.occupancy_30d_pct || 0).toFixed(1)}%`}
            sub="Bryte rooms only"
            accent="text-emerald-400"
          />
          <StatCard
            icon={DollarSign}
            label="30d Revenue"
            value={`$${(revenue?.total_revenue_usd || 0).toFixed(2)}`}
            sub={`${revenue?.total_billed_nights || 0} billed nights · 100% getbitsy`}
            accent="text-amber-400"
          />
          <StatCard
            icon={TrendingUp}
            label="Hardware Payback"
            value={`${(revenue?.hardware_roi?.payback_progress_pct || 0).toFixed(1)}%`}
            sub={revenue?.hardware_roi?.status === 'profit'
              ? `+$${revenue.hardware_roi.net_position_usd.toFixed(2)} profit`
              : `$${(revenue?.hardware_roi?.total_capex_usd || 0).toFixed(0)} CAPEX · amortizing`}
            accent={revenue?.hardware_roi?.status === 'profit' ? 'text-emerald-400' : 'text-purple-400'}
          />
        </div>

        {/* Live tier rates */}
        <section>
          <h2 className="text-xs uppercase tracking-wider text-[#94a3b8] mb-3 flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-cyan-400" /> Live Per-Night Rates
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {pricing?.tiers && Object.entries(pricing.tiers).map(([tier, data]) => (
              <TierRateCard
                key={tier}
                tier={tier}
                data={data}
                occupancy={pricing.occupancy_30d_pct || 0}
              />
            ))}
          </div>
          <p className="text-[9px] text-[#475569] mt-2 font-mono">{pricing?.formula}</p>
        </section>

        {/* Installed units */}
        <section>
          <h2 className="text-xs uppercase tracking-wider text-[#94a3b8] mb-3">Installed Units</h2>
          <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl overflow-hidden">
            {mattresses.length === 0 ? (
              <div className="p-8 text-center">
                <BedDouble className="w-8 h-8 text-[#1a2235] mx-auto mb-2" />
                <p className="text-xs text-[#64748b]">No Bryte mattresses installed yet</p>
                <p className="text-[10px] text-[#475569] mt-1">
                  Register one via <code>POST /api/bryte/register</code>
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[#1a2235]">
                {mattresses.map((m) => {
                  const meta = TIER_META[m.tier] || TIER_META.balance;
                  return (
                    <div
                      key={m.id}
                      className="p-4 flex items-center gap-4 flex-wrap"
                      data-testid={`mattress-${m.id}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                        <div className={`w-10 h-10 rounded-xl ${meta.bg} flex items-center justify-center`}>
                          <BedDouble className={`w-5 h-5 ${meta.text}`} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold">Room {m.room_number}</p>
                          <p className="text-[10px] text-[#64748b] font-mono">{m.device_id}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <Badge className={`${meta.bg} ${meta.text} text-[9px] border-0`}>{m.tier_label}</Badge>
                        <div className="text-right">
                          <p className="text-[9px] text-[#64748b] uppercase">Current rate</p>
                          <p className={`text-sm font-bold font-mono ${meta.text}`}>
                            ${m.current_surge_rate_usd?.toFixed(2) || m.floor_rate_usd.toFixed(2)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[9px] text-[#64748b] uppercase">Nights billed</p>
                          <p className="text-sm font-bold font-mono text-emerald-400">{m.total_nights_billed}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[9px] text-[#64748b] uppercase">Revenue</p>
                          <p className="text-sm font-bold font-mono">${(m.total_revenue_usd || 0).toFixed(2)}</p>
                        </div>
                      </div>

                      {(m.last_bed_occupied || m.last_sleep_score) && (
                        <div className="flex items-center gap-3 text-[10px] text-[#94a3b8] ml-auto">
                          {m.last_bed_occupied && (
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3 text-rose-400" /> Occupied
                            </span>
                          )}
                          {m.last_sleep_score != null && (
                            <span className="flex items-center gap-1">
                              <Activity className="w-3 h-3 text-cyan-400" />
                              Sleep {m.last_sleep_score}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* Recent settlements */}
        <section>
          <h2 className="text-xs uppercase tracking-wider text-[#94a3b8] mb-3">Recent Nights (30d)</h2>
          <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl overflow-hidden">
            {(revenue?.recent_nights || []).length === 0 ? (
              <p className="p-6 text-center text-xs text-[#64748b]">No billed nights yet</p>
            ) : (
              <div className="divide-y divide-[#1a2235] text-xs">
                <div className="grid grid-cols-5 gap-2 p-3 text-[9px] uppercase tracking-wider text-[#64748b]">
                  <span>Date</span><span>Room</span><span>Tier</span><span>Rate</span><span>Status</span>
                </div>
                {(revenue?.recent_nights || []).slice(0, 20).map((n) => (
                  <div key={n.id} className="grid grid-cols-5 gap-2 p-3 items-center" data-testid={`night-${n.id}`}>
                    <span className="font-mono">{n.date}</span>
                    <span>Room {n.room_number}</span>
                    <span className={TIER_META[n.tier]?.text || ''}>{TIER_META[n.tier]?.label || n.tier}</span>
                    <span className="font-mono font-bold text-cyan-400">${n.rate_usd.toFixed(2)}</span>
                    <span>
                      <Badge className={n.settlement_status === 'settled' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}>
                        {n.settlement_status}
                      </Badge>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Business model info */}
        <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-4">
          <p className="text-[10px] uppercase text-[#64748b] tracking-wider mb-2">Business Model</p>
          <div className="flex items-center gap-4 text-xs flex-wrap">
            <span>
              getbitsy <span className="font-mono font-bold text-cyan-400">100%</span>
            </span>
            <span className="text-[#1a2235]">│</span>
            <span className="text-[#94a3b8]">
              Asset-owner model: getbitsy buys wholesale from Bryte, deploys, collects full lease
            </span>
          </div>
          <div className="mt-3 pt-3 border-t border-[#1a2235] grid grid-cols-1 md:grid-cols-3 gap-3 text-[10px]">
            <div>
              <p className="text-[#64748b] uppercase">Total CAPEX</p>
              <p className="font-mono font-bold text-purple-400 text-sm">
                ${(revenue?.hardware_roi?.total_capex_usd || 0).toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-[#64748b] uppercase">Lifetime Revenue</p>
              <p className="font-mono font-bold text-emerald-400 text-sm">
                ${(revenue?.hardware_roi?.total_lifetime_revenue_usd || 0).toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-[#64748b] uppercase">Net Position</p>
              <p className={`font-mono font-bold text-sm ${
                (revenue?.hardware_roi?.net_position_usd || 0) >= 0 ? 'text-emerald-400' : 'text-amber-400'
              }`}>
                {(revenue?.hardware_roi?.net_position_usd || 0) >= 0 ? '+' : ''}
                ${(revenue?.hardware_roi?.net_position_usd || 0).toFixed(2)}
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-[#1a2235] flex items-center gap-3 text-[10px] text-[#94a3b8]">
            <Flame className="w-3 h-3 text-amber-400" />
            <Snowflake className="w-3 h-3 text-cyan-400" />
            <span>Cooling + warming unlocked as part of lease · IoT-verified billing</span>
          </div>
        </div>
      </div>
    </div>
  );
}
